/**
 * Class & booking analytics (Analytics spec §3.6) — fill rate, attendance,
 * no-show / late-cancel per session, and waitlist demand (unmet demand → which
 * classes to add). Reads 0147 views. Owner/manager. Backend data, read-only.
 */
import { getClassPerformance, getWaitlistDemand } from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { useFormatters } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

export function ClassAnalytics(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const on = { enabled: !!tenantId };

  const classes = useQuery({
    queryKey: ['an_classes', tenantId],
    queryFn: () => getClassPerformance(supabase, tenantId),
    ...on,
  });
  const waitlist = useQuery({
    queryKey: ['an_waitlist', tenantId],
    queryFn: () => getWaitlistDemand(supabase, tenantId),
    ...on,
  });

  return (
    <div className="grid gap-5">
      <ExpandableCard
        title={t('analytics.intel.classPerformance')}
        subtitle={t('analytics.intel.classSubtitle')}
        loading={classes.isLoading}
        empty={(classes.data ?? []).length === 0 ? t('analytics.intel.noClasses') : undefined}
      >
        {(classes.data ?? []).length === 0 ? (
          <EmptyState icon={CalendarDays} title={t('analytics.intel.noClasses')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('analytics.intel.session')}</TH>
                  <TH numeric>{t('analytics.intel.capacity')}</TH>
                  <TH numeric>{t('analytics.intel.booked')}</TH>
                  <TH numeric>{t('analytics.intel.attended')}</TH>
                  <TH numeric>{t('analytics.intel.noShow')}</TH>
                  <TH numeric>{t('analytics.intel.fillRate')}</TH>
                </tr>
              </THead>
              <tbody>
                {(classes.data ?? []).slice(0, 100).map((r) => (
                  <TR key={r.session_id}>
                    <TD className="font-medium">
                      {r.class_name ?? (r.is_pt ? t('analytics.intel.pt') : '—')}
                      <span className="ml-2 text-xs text-muted">
                        {fmt.date(r.starts_at, { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </TD>
                    <TD numeric className="text-muted">
                      {r.capacity}
                    </TD>
                    <TD numeric>{r.booked}</TD>
                    <TD numeric className="text-muted">
                      {r.attended}
                    </TD>
                    <TD numeric className="text-muted">
                      {r.no_show}
                    </TD>
                    <TD numeric>
                      <Chip
                        tone={
                          (r.fill_rate_pct ?? 0) >= 80
                            ? 'success'
                            : (r.fill_rate_pct ?? 0) >= 40
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {r.fill_rate_pct ?? 0}%
                      </Chip>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </ExpandableCard>

      <ExpandableCard
        title={t('analytics.intel.waitlistDemand')}
        subtitle={t('analytics.intel.waitlistSubtitle')}
        loading={waitlist.isLoading}
        empty={(waitlist.data ?? []).length === 0 ? t('analytics.intel.noWaitlist') : undefined}
      >
        {(waitlist.data ?? []).length === 0 ? (
          <EmptyState icon={CalendarDays} title={t('analytics.intel.noWaitlist')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('analytics.intel.session')}</TH>
                  <TH numeric>{t('analytics.intel.waiting')}</TH>
                  <TH numeric>{t('analytics.intel.promoted')}</TH>
                  <TH numeric>{t('analytics.intel.waitlistTotal')}</TH>
                </tr>
              </THead>
              <tbody>
                {(waitlist.data ?? []).slice(0, 100).map((r) => (
                  <TR key={r.class_session_id}>
                    <TD className="font-medium">
                      {r.class_name ?? '—'}
                      <span className="ml-2 text-xs text-muted">
                        {fmt.date(r.starts_at, { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </TD>
                    <TD numeric>{r.waiting}</TD>
                    <TD numeric className="text-muted">
                      {r.promoted}
                    </TD>
                    <TD numeric>{r.waitlist_total}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </ExpandableCard>
    </div>
  );
}
